import { useState } from 'react';
import { css } from '@emotion/css';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Heading } from '@spark-web/heading';
import { HomeIcon, InformationCircleIcon } from '@spark-web/icon';
import { ContentDialog } from '@spark-web/modal-dialog';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextLinkButton } from '@spark-web/text-link';
import { LABELS } from '@utils';
import EligibilityPageHeading from './EligibilityPageHeading';
import EligibilityStepHeader from './EligibilityStepHeader';

type ConfirmInstallationAddressProps = {
  address: string;
  productName: string;
  vendorName: string;
  onConfirm: () => void;
  onBack: () => void;
  onNotCorrectAddress: () => void;
};

function ConfirmInstallationAddress({
  address,
  productName,
  vendorName,
  onConfirm,
  onBack,
  onNotCorrectAddress,
}: ConfirmInstallationAddressProps) {
  const [isNotCorrectModalOpen, setIsNotCorrectModalOpen] = useState(false);

  const handleNotCorrectAddress = () => {
    setIsNotCorrectModalOpen(true);
    onNotCorrectAddress();
  };

  return (
    <Stack padding={{ tablet: 'xlarge' }} gap="xxlarge">
      <EligibilityStepHeader currentStep={2} totalSteps={5} />

      <EligibilityPageHeading productName={productName}>
        <Heading level="1">Confirm your installation address</Heading>
      </EligibilityPageHeading>

      <Stack gap="xxlarge">
        <Text size="standard">
          This is the address{' '}
          <Text as="span" weight="semibold" inline>
            {vendorName}
          </Text>{' '}
          gave to us. Please check it&apos;s correct before you continue.
        </Text>

        <Stack gap="large">
          <Box
            display="flex"
            alignItems="start"
            gap="medium"
            padding="large"
            borderRadius="medium"
            background="surfaceMuted"
            borderWidth="standard"
            border="standard"
          >
            <Box flexShrink={0} paddingTop="xsmall">
              <HomeIcon size="small" tone="primary" />
            </Box>
            <Stack gap="large">
              <Box className={css({ textTransform: 'uppercase' })}>
                <Text size="xsmall" weight="semibold">
                  Installation address
                </Text>
              </Box>
              {address ? (
                <Text size="xlarge">{address}</Text>
              ) : (
                <Text size="xlarge" tone="muted">
                  Address not available
                </Text>
              )}
            </Stack>
          </Box>

          <Text size="small">
            <TextLinkButton tone="primaryActive" weight="semibold" onClick={handleNotCorrectAddress}>
              Not the correct address?
            </TextLinkButton>
          </Text>
        </Stack>

        <Box
          display="flex"
          alignItems="start"
          gap="medium"
          padding="large"
          borderRadius="medium"
          background="infoMuted"
        >
          <Box flexShrink={0}>
            <InformationCircleIcon size="xsmall" tone="info" />
          </Box>
          <Text size="small">
            By continuing, you consent to Brighte running a check to establish who owns the property. Only the owner of
            the property can apply for the loan.
          </Text>
        </Box>
      </Stack>

      <Stack gap="large">
        <Button onClick={onConfirm} tone="primary">
          {LABELS.CONTINUE}
        </Button>
        <Button prominence="none" tone="neutral" onClick={onBack}>
          Back
        </Button>
      </Stack>

      <ContentDialog
        title="Not the right address?"
        isOpen={isNotCorrectModalOpen}
        onToggle={() => setIsNotCorrectModalOpen(open => !open)}
        footer={<Button onClick={() => setIsNotCorrectModalOpen(false)}>Got it</Button>}
      >
        <Text>
          This address was provided by{' '}
          <Text as="span" weight="semibold" inline>
            {vendorName}
          </Text>
          , so they&apos;ll need to update it for you. Get in touch with them to have it corrected, they&apos;ll send a
          new application referral to complete.
        </Text>
      </ContentDialog>
    </Stack>
  );
}

export default ConfirmInstallationAddress;
